import React, { useEffect, useState } from 'react'
import {
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography
} from '@material-ui/core'
import { Box } from '@mui/material'
import { ALL_DATES_FILTER } from 'store/event'
import { useDispatch, useSelector } from 'react-redux'
import { colors } from 'utils/colors'
import talkSlice, { deleteTalkById, fetchConferenceTalks } from 'store/talk'
import BasicTable from 'components/BasicTable'
import ResourceNotFound from 'components/ResourceNotFound'
import { DeleteOutlineOutlined as DeleteIcon, EditOutlined as EditIcon } from '@material-ui/icons'
import getTableOptions from 'utils/getTableOptions'
import { renderCol } from 'utils/renderColumns'
import speakerSlice, { getSpeakers } from 'store/speaker'
import { getVenuesInConference } from 'store/venue'
import { useConfirm } from 'material-ui-confirm'
import { useHistory } from 'react-router-dom'
import moment from 'moment-timezone'
import { convertTimestampToLocalString } from 'utils/times'
import TextField from '@material-ui/core/TextField'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { speakerStatus } from 'constants'

const TalksListing = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const confirm = useConfirm()
  const { user } = useSelector((state) => state.auth)
  const {
    talks,
    selectedDate,
    selectedSpeaker,
    selectedVenue,
    talkSpeakers,
    talkVenues,
    inProgress
  } = useSelector((state) => state.talk)
  const { conference } = useSelector((state) => state.conference)
  const { venues } = useSelector((state) => state.venue)
  const { speakers } = useSelector((state) => state.speaker)

  const [formattedTalks, setFormattedTalks] = useState(talks || [])
  const [dateFilters, setDateFilters] = useState([ALL_DATES_FILTER])

  useEffect(() => {
    dispatch(fetchConferenceTalks(user?.conferenceId))
    return () => dispatch(talkSlice.actions.setTalks([]))
  }, [])

  useEffect(() => {
    const filteredTalksByDate =
      selectedDate === ALL_DATES_FILTER
        ? talks
        : talks?.filter(
            (talk) =>
              moment(talk.startDate).tz(conference?.timezone).format('MM/D/YYYY') === selectedDate
          )

    const filteredTalksByVenue =
      selectedVenue === 'All'
        ? filteredTalksByDate
        : filteredTalksByDate.filter((talk) => talk.venueId === selectedVenue)

    const filterTalksBySpeaker = selectedSpeaker?.id
      ? filteredTalksByVenue?.filter((talk) => talk?.speakers.includes(selectedSpeaker?.id))
      : filteredTalksByVenue

    setFormattedTalks(filterTalksBySpeaker)
  }, [talks, selectedDate, selectedVenue, selectedSpeaker])

  useEffect(() => {
    if (talks.length && conference) {
      const DateFilters = talks.reduce((filters, talk) => {
        const date = moment(talk.startDate).tz(conference?.timezone).format('MM/DD/YYYY')
        if (!filters.includes(date)) {
          filters.push(date)
        }
        return filters
      }, [])

      if (!DateFilters.includes(selectedDate)) {
        dispatch(talkSlice.actions.setSelectedDate(ALL_DATES_FILTER))
      }
      setDateFilters([ALL_DATES_FILTER, ...DateFilters])
    }
  }, [talks, conference])

  useEffect(() => {
    dispatch(getVenuesInConference(user?.conferenceId))
    const unsub = getSpeakers(user?.conferenceId, (data) => {
      dispatch(speakerSlice.actions.setSpeakers(data))
    })
    return () => unsub && unsub()
  }, [dispatch, user?.conferenceId])

  const tableHead = [
    {
      name: 'Sr #',
      options: {
        customBodyRender: (value) => <div style={{ minWidth: '90px' }}>{value}</div>
      }
    },
    'ABS/POS #',
    'Talk Title',
    {
      name: 'Start Time',
      options: {
        customBodyRender: (value) => (
          <div style={{ minWidth: '130px' }}>
            {convertTimestampToLocalString(value, conference?.timezone, conference?.is24hrFormat)}
          </div>
        )
      }
    },
    {
      name: 'End Time',
      options: {
        customBodyRender: (value) => (
          <div style={{ minWidth: '130px' }}>
            {convertTimestampToLocalString(value, conference?.timezone, conference?.is24hrFormat)}
          </div>
        )
      }
    },
    'Venue',
    'Speakers',
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    }
  ]

  const formatTableData = (talks) =>
    talks?.map((talk) => {
      const talkVenue = venues?.find((type) => type.id === talk?.venueId)
      let talkSpeakers = talk?.speakers
        ?.slice(0, 3)
        ?.map((speaker) => {
          const talkSpeaker = speakers?.find((s) => s.id === speaker)
          return talkSpeaker?.fullName ? talkSpeaker.fullName : null
        })
        .join(', ')
      if (talk?.speakers?.length > 2) talkSpeakers += ' and others'
      let abs_pos_no = ''
      if (talk?.isAbstract && talk?.posterNumber) {
        abs_pos_no = `${conference?.posterPrefix} - ${talk.posterNumber}`
      } else if (talk?.isAbstract && talk?.abstractNumber) {
        abs_pos_no = `${conference?.abstractPrefix} - ${talk.abstractNumber}`
      }
      return [
        talk?.srNo,
        abs_pos_no,
        talk?.title ?? 'N/A',
        talk?.startDate ?? 'N/A',
        talk?.endDate ?? 'N/A',
        talkVenue?.venueText,
        talkSpeakers,
        <Box style={{ width: 80 }}>
          <Tooltip title="Edit">
            <IconButton
              className={classes.iconBtn}
              onClick={() => {
                const redirectUrl = `/admin/events/${talk?.eventId}/${
                  talk?.sessionId ? `sessions/${talk?.sessionId}/talks` : 'talks'
                }/edit/${talk?.id}`
                history.push(redirectUrl)
              }}>
              <EditIcon color="action" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              className={classes.iconBtn}
              onClick={async () => {
                await confirm({
                  title: 'Delete Talk',
                  description: 'Are you sure you want to delete this Talk ?'
                })
                await dispatch(deleteTalkById(talk.id))
              }}>
              <DeleteIcon color="action" />
            </IconButton>
          </Tooltip>
        </Box>
      ]
    })

  const options = getTableOptions()

  if (inProgress) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '3rem' }}>
        <CircularProgress size={40} />
      </Box>
    )
  }

  return (
    <Box>
      <Box className={classes.headBar}>
        <Typography variant="h4">Conference Talks</Typography>
      </Box>

      <Box className={classes.customCard}>
        <Typography className={classes.cardsInfo}>
          Use fields given below to filter the talks
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Box className={classes.dropdown}>
            <Typography variant="body2" className={classes.dropdownLabel}>
              Talk Date
            </Typography>
            <FormControl fullWidth variant="outlined" size="medium">
              <Select
                value={selectedDate}
                onChange={(event) => {
                  const value = event.target.value
                  dispatch(talkSlice.actions.setSelectedDate(value))
                }}>
                {dateFilters?.map((filter) => (
                  <MenuItem value={filter} key={filter}>
                    {filter}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box className={classes.dropdown}>
            <Typography variant="body2" className={classes.dropdownLabel}>
              Venue
            </Typography>
            <FormControl fullWidth variant="outlined" size="medium">
              <Select
                value={selectedVenue}
                onChange={(event) => {
                  const value = event.target.value
                  dispatch(talkSlice.actions.setSelectedVenue(value))
                }}>
                <MenuItem value="All">All</MenuItem>
                {talkVenues?.map((venue) => (
                  <MenuItem value={venue.id} key={venue.id}>
                    {venue.venueText}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box className={classes.dropdown}>
            <Typography variant="body2" className={classes.dropdownLabel}>
              Speaker
            </Typography>

            <Autocomplete
              variant="outlined"
              options={talkSpeakers}
              onChange={(e, value) => {
                dispatch(talkSlice.actions.setSelectedSpeaker(value))
              }}
              getOptionLabel={(option) => option?.fullName}
              value={selectedSpeaker}
              renderOption={(option) => (
                <li className={classes.listItem}>
                  {option?.isSubject && option?.fullName
                    ? option?.fullName
                    : option?.isSubject && !option?.fullName
                    ? option.email
                    : option.fullName}
                  {option?.isSubject && option?.status === speakerStatus.PENDING && (
                    <Chip
                      size="small"
                      className={classes.chip}
                      color="primary"
                      label={
                        <Typography className={classes.chipLabel}>
                          {option?.status === speakerStatus.PENDING ? 'Not Approved' : 'Approved'}
                        </Typography>
                      }
                    />
                  )}
                </li>
              )}
              renderInput={(params) => (
                <TextField placeholder="Select speaker" {...params} variant="outlined" />
              )}
            />
          </Box>
        </Box>
      </Box>
      <Box>
        {formattedTalks?.length ? (
          <Paper variant="outlined" style={{ marginTop: 20 }}>
            <BasicTable
              data={formatTableData(formattedTalks || [])}
              columns={tableHead}
              options={options}
            />
          </Paper>
        ) : (
          <ResourceNotFound message="No talks available" />
        )}
      </Box>
    </Box>
  )
}

export default TalksListing

const useStyles = makeStyles({
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  chip: {
    background: '#c62828',
    color: '#FFFFFF'
  },
  chipLabel: {
    fontSize: '13px',
    letterSpacing: '0.5px',
    fontWeight: 'bold'
  },
  headBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '1rem',
    marginBottom: '1.5rem'
  },
  customCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1.25rem',
    border: '1px solid #bdbdbd',
    borderRadius: '12px'
  },
  cardsInfo: {
    color: colors.indigo,
    fontSize: '16px',
    fontWeight: 500
  },
  subText: {
    marginTop: '4px',
    fontSize: '12px',
    color: '#757575'
  },
  iconBtn: {
    padding: '8px'
  },
  dropdown: {
    width: '320px',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.2rem'
  },
  dropdownLabel: {
    fontSize: '12px',
    color: '#616161',
    whiteSpace: 'nowrap'
  }
})
